import React, { useState, useEffect, useCallback } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';

/**
 * User access.
 *
 * Reception decides who may open this module; this screen decides who is
 * actually let in and what they see. Nobody is added automatically — an admin
 * picks them off the reception user list here, and anyone who has not been
 * added is refused, whatever reception says.
 *
 * The one exception is reception admins, created on their first visit. Without
 * that nobody could ever get in to approve the first user.
 */
const AccessTab = () => {
    const [scopes, setScopes] = useState([]);
    const [units, setUnits] = useState([]);
    const [receptionUsers, setReceptionUsers] = useState([]);
    const [picked, setPicked] = useState('');
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const [s, b, r] = await Promise.all([
                stockReorderApi.listScopes(),
                stockReorderApi.listAllBusinessUnits(),
                stockReorderApi.listReceptionUsers()
            ]);
            setScopes(s.scopes || []);
            setUnits(b.business_units || []);
            setReceptionUsers(r.users || []);
        } catch (err) {
            setError(errorMessage(err, 'Could not load user access'));
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (userId, changes) => {
        setSavingId(userId);
        setError('');
        try {
            const updated = await stockReorderApi.setScope(userId, changes);
            setScopes((list) => list.map((s) => (s.user_id === userId ? updated : s)));
        } catch (err) {
            setError(errorMessage(err, 'Could not save that change'));
            await load();
        } finally {
            setSavingId(null);
        }
    };

    const toggleBranch = (scope, buId) => {
        const current = scope.business_unit_ids || [];
        const next = current.includes(buId)
            ? current.filter((id) => id !== buId)
            : [...current, buId];
        save(scope.user_id, { businessUnitIds: next });
    };

    const addUser = async () => {
        const user = receptionUsers.find((u) => u.user_id === picked);
        if (!user) return;
        setAdding(true);
        setError('');
        try {
            await stockReorderApi.createScope({
                userId: user.user_id,
                displayName: user.display_name,
                email: user.email
            });
            setPicked('');
            await load();
        } catch (err) {
            setError(errorMessage(err, 'Could not add that user'));
        } finally {
            setAdding(false);
        }
    };

    const remove = async (userId) => {
        if (!window.confirm('Remove this profile? Their branch assignments are lost.')) return;
        setBusy(true);
        try {
            await stockReorderApi.deleteScope(userId);
            await load();
        } catch (err) {
            setError(errorMessage(err, 'Could not remove that profile'));
        } finally {
            setBusy(false);
        }
    };

    // Anyone already registered is dropped from the picker — the API rejects
    // duplicates, so offering them would only produce an error.
    const available = receptionUsers.filter(
        (u) => !scopes.some((s) => s.user_id === u.user_id)
    );

    return (
        <div className="sr-stack">
            <section className="sr-card">
                <h2 className="sr-card-title">Add a user</h2>
                <p className="sr-muted">
                    Pick someone from reception. Until they are added here they cannot use this
                    module, even if they can see the card.
                </p>

                <div className="sr-field">
                    <label htmlFor="sr-add-user">Reception user</label>
                    <select
                        id="sr-add-user"
                        value={picked}
                        onChange={(e) => setPicked(e.target.value)}
                        disabled={busy || adding || available.length === 0}
                    >
                        <option value="">
                            {available.length === 0
                                ? 'Every reception user is already registered'
                                : 'Select a reception user…'}
                        </option>
                        {available.map((u) => (
                            <option key={u.user_id} value={u.user_id}>
                                {u.display_name || u.user_id}
                                {u.email ? ` — ${u.email}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
                <button
                    className="sr-btn sr-btn-sm"
                    onClick={addUser}
                    disabled={!picked || adding}
                >
                    {adding ? 'Adding…' : 'Add user'}
                </button>
            </section>

            <section className="sr-card">
                <h2 className="sr-card-title">Branch access</h2>
                <p className="sr-muted">
                    Module admins see every branch regardless of what is ticked here.
                </p>

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
                {busy && <div className="sr-empty">Loading…</div>}

                {!busy && scopes.length === 0 && (
                    <div className="sr-empty">
                        Nobody is registered yet. Add a user above to give them access.
                    </div>
                )}

                {scopes.map((scope) => (
                    <div key={scope.user_id} className="sr-scope-row">
                        <div className="sr-scope-head">
                            <div>
                                <strong>{scope.display_name || scope.user_id}</strong>
                                {scope.is_admin && (
                                    <span className="sr-tag sr-tag-ok">module admin</span>
                                )}
                                {scope.is_active === false && (
                                    <span className="sr-tag">deactivated</span>
                                )}
                                {savingId === scope.user_id && (
                                    <span className="sr-muted"> saving…</span>
                                )}
                            </div>
                            <div className="sr-nowrap">
                                <label className="sr-check">
                                    <input
                                        type="checkbox"
                                        checked={scope.is_active !== false}
                                        onChange={(e) =>
                                            save(scope.user_id, { isActive: e.target.checked })
                                        }
                                    />
                                    Active
                                </label>
                                <label className="sr-check">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(scope.allow_restricted_suppliers)}
                                        onChange={(e) =>
                                            save(scope.user_id, {
                                                allowRestrictedSuppliers: e.target.checked
                                            })
                                        }
                                    />
                                    Restricted suppliers
                                </label>
                                <button
                                    className="sr-btn sr-btn-sm sr-btn-danger"
                                    onClick={() => remove(scope.user_id)}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <div className="sr-checklist">
                            {units.map((bu) => (
                                <label key={bu.id} className="sr-check">
                                    <input
                                        type="checkbox"
                                        checked={(scope.business_unit_ids || []).includes(bu.id)}
                                        onChange={() => toggleBranch(scope, bu.id)}
                                        disabled={scope.is_admin}
                                    />
                                    {bu.name}
                                </label>
                            ))}
                            {units.length === 0 && (
                                <span className="sr-muted">No branches configured yet.</span>
                            )}
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
};

export default AccessTab;
